import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { XMarkIcon, CalendarIcon, UserIcon, BuildingStorefrontIcon, TagIcon, DocumentTextIcon, ShareIcon, ChevronLeftIcon, ChevronRightIcon, ArrowsPointingOutIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { PhotoWithDetails } from '../types';
import PlaceholderImage from './PlaceholderImage';
import ShareModal from './ShareModal';
import { useAuth } from '../contexts/AuthContext';

interface PhotoModalProps {
  photo: PhotoWithDetails;
  photos: PhotoWithDetails[];
  currentIndex: number;
  onClose: () => void;
  onNavigate: (newIndex: number) => void;
}

const PhotoModal: React.FC<PhotoModalProps> = ({ photo, photos, currentIndex, onClose, onNavigate }) => {
  const { token } = useAuth();
  const [imageError, setImageError] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [lastTap, setLastTap] = useState<number>(0);
  const [isMobile, setIsMobile] = useState(false);

  // Минимальное расстояние для swipe (в пикселях)
  const minSwipeDistance = 50;

  // Reset image error when photo changes
  useEffect(() => {
    setImageError(false);
  }, [photo.id]);

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const canGoPrevious = currentIndex > 0;
  const canGoNext = currentIndex < photos.length - 1;

  const handlePrevious = () => {
    if (canGoPrevious) {
      onNavigate(currentIndex - 1);
    }
  };

  const handleNext = () => {
    if (canGoNext) {
      onNavigate(currentIndex + 1);
    }
  };

  // Функции для обработки swipe-жестов
  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null); // Сбрасываем touchEnd при начале нового касания
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && canGoNext) {
      handleNext();
    }
    if (isRightSwipe && canGoPrevious) {
      handlePrevious();
    }
  };

  // Обработка двойного касания для полноэкранного режима
  const handleImageClick = () => {
    const currentTime = new Date().getTime();
    const tapLength = currentTime - lastTap;
    if (tapLength < 500 && tapLength > 0) {
      // Двойное касание
      setIsFullscreen(!isFullscreen);
    }
    setLastTap(currentTime);
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      } else if (event.key === 'ArrowLeft') {
        handlePrevious();
      } else if (event.key === 'ArrowRight') {
        handleNext();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose, handlePrevious, handleNext]);
  const getPhotoUrl = (photoId: number) => {
    return `/api/photos/image/${photoId}?token=${encodeURIComponent(token || '')}`;
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4"
      onClick={handleBackdropClick}
    >
      <div className={`bg-white shadow-2xl overflow-hidden flex flex-col transition-all duration-300 ${
        isFullscreen
          ? 'fixed inset-0 rounded-none max-w-none max-h-none'
          : 'rounded-lg sm:rounded-xl max-w-sm sm:max-w-4xl lg:max-w-6xl max-h-[98vh] sm:max-h-[95vh]'
      }`}>
        {/* Header */}
        {!isFullscreen && (
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-3 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 sm:space-x-3">
                <div className="bg-gradient-to-br from-indigo-500 to-purple-600 p-1.5 sm:p-2 rounded-lg">
                  <svg className="w-4 h-4 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-sm sm:text-xl font-bold text-gray-900">Просмотр фотографии</h2>
                  <p className="text-xs sm:text-sm text-gray-500">
                    {currentIndex + 1} из {photos.length}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-1 sm:space-x-2">
                <button
                  onClick={() => setShowInfo(!showInfo)}
                  className="p-1.5 sm:p-2 text-gray-600 hover:text-indigo-600 hover:bg-white rounded-lg transition-all sm:hidden"
                  title="Информация"
                >
                  <InformationCircleIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setIsFullscreen(true)}
                  className="p-1.5 sm:p-2 text-gray-600 hover:text-indigo-600 hover:bg-white rounded-lg transition-all"
                  title="Полный экран"
                >
                  <ArrowsPointingOutIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>
                <button
                  onClick={() => setShowShareModal(true)}
                  className="p-1.5 sm:p-2 text-gray-600 hover:text-indigo-600 hover:bg-white rounded-lg transition-all"
                  title="Поделиться"
                >
                  <ShareIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>
                <button
                  onClick={onClose}
                  className="p-1.5 sm:p-2 text-gray-600 hover:text-red-600 hover:bg-white rounded-lg transition-all"
                  title="Закрыть"
                >
                  <XMarkIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                </button>
              </div>
            </div>
          </div>
        )}

        <div className={`flex flex-1 overflow-hidden ${
          isFullscreen || (!showInfo && isMobile)
            ? 'flex-col'
            : 'flex-col lg:flex-row'
        }`}>
          {/* Photo */}
          <div
            className={`bg-gray-900 flex items-center justify-center relative ${
              isFullscreen
                ? 'flex-1 p-0'
                : 'flex-1 p-2 sm:p-4'
            }`}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            {imageError ? (
              <div className="w-full h-96 flex items-center justify-center">
                <PlaceholderImage
                  className="w-64 h-64"
                  text="Изображение недоступно"
                />
              </div>
            ) : (
              <img
                src={getPhotoUrl(photo.id)}
                alt={`Фото от ${photo.agent?.name || 'Неизвестно'}`}
                className="max-w-full max-h-full object-contain cursor-pointer"
                onError={() => setImageError(true)}
                onClick={handleImageClick}
              />
            )}

            {/* Navigation buttons */}
            {canGoPrevious && (
              <button
                onClick={handlePrevious}
                className={`absolute left-2 sm:left-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-90 hover:bg-opacity-100 text-gray-700 hover:text-indigo-600 rounded-full shadow-lg transition-all hover:scale-110 min-h-[48px] min-w-[48px] touch-manipulation ${
                  isFullscreen ? 'p-2' : 'p-3'
                }`}
                title="Предыдущая фотография (←)"
              >
                <ChevronLeftIcon className={isFullscreen ? "h-6 w-6" : "h-8 w-8"} />
              </button>
            )}

            {canGoNext && (
              <button
                onClick={handleNext}
                className={`absolute right-2 sm:right-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-90 hover:bg-opacity-100 text-gray-700 hover:text-indigo-600 rounded-full shadow-lg transition-all hover:scale-110 min-h-[48px] min-w-[48px] touch-manipulation ${
                  isFullscreen ? 'p-2' : 'p-3'
                }`}
                title="Следующая фотография (→)"
              >
                <ChevronRightIcon className={isFullscreen ? "h-6 w-6" : "h-8 w-8"} />
              </button>
            )}

            {/* Fullscreen controls overlay */}
            {isFullscreen && (
              <div className="absolute top-4 right-4 flex space-x-2">
                <button
                  onClick={() => setShowShareModal(true)}
                  className="p-2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white rounded-full transition-all"
                  title="Поделиться"
                >
                  <ShareIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setIsFullscreen(false)}
                  className="p-2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white rounded-full transition-all"
                  title="Выйти из полного экрана"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            )}
          </div>

          {/* Info Panel */}
          {!isFullscreen && (showInfo || !isMobile) && (
            <div className={`bg-gradient-to-b from-gray-50 to-white overflow-y-auto border-l border-gray-200 ${
              isMobile
                ? 'w-full p-4 max-h-64 border-t border-l-0'
                : 'w-full lg:w-96 p-6'
            }`}>
            <div className="space-y-4">
              {/* Date */}
              <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                <div className="flex items-start">
                  <div className="bg-indigo-50 p-2 rounded-lg mr-3">
                    <CalendarIcon className="h-5 w-5 text-indigo-600" />
                  </div>
                  <div className="flex-1">
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Дата съемки</div>
                    <div className="text-sm text-gray-900 mt-1">
                      {format(new Date(photo.photoDate), 'dd MMMM yyyy, HH:mm', { locale: ru })}
                    </div>
                  </div>
                </div>
              </div>

              {/* Agent */}
              {photo.agent && (
                <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                  <div className="flex items-start">
                    <div className="bg-blue-50 p-2 rounded-lg mr-3">
                      <UserIcon className="h-5 w-5 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Торговый представитель</div>
                      <div className="text-sm text-gray-900 mt-1 font-medium">{photo.agent.name}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Client */}
              <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                <div className="flex items-start">
                  <div className="bg-green-50 p-2 rounded-lg mr-3">
                    <BuildingStorefrontIcon className="h-5 w-5 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Клиент</div>
                    {photo.client ? (
                      <div className="mt-1">
                        <div className="text-sm text-gray-900 font-medium">{photo.client.name}</div>
                        {photo.client.address && (
                          <div className="text-xs text-gray-600 mt-1">{photo.client.address}</div>
                        )}
                        {photo.client.phone && (
                          <div className="text-xs text-gray-600">{photo.client.phone}</div>
                        )}
                        {photo.client.contactPerson && (
                          <div className="text-xs text-gray-600">Контакт: {photo.client.contactPerson}</div>
                        )}
                      </div>
                    ) : (
                      <div className="text-sm text-gray-500 italic mt-1">Клиент не указан</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Category */}
              {photo.category && (
                <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                  <div className="flex items-start">
                    <div className="bg-purple-50 p-2 rounded-lg mr-3">
                      <TagIcon className="h-5 w-5 text-purple-600" />
                    </div>
                    <div className="flex-1">
                      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Категория</div>
                      <div className="mt-1">
                        <div className="text-sm text-gray-900 font-medium">{photo.category.name}</div>
                        {photo.category.description && (
                          <div className="text-xs text-gray-600 mt-1">{photo.category.description}</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Technical Info */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="flex items-start">
                  <div className="bg-gray-200 p-2 rounded-lg mr-3">
                    <DocumentTextIcon className="h-5 w-5 text-gray-600" />
                  </div>
                  <div className="flex-1">
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Техническая информация</div>
                    <div className="text-xs text-gray-600 space-y-1">
                      <div className="flex justify-between">
                        <span className="text-gray-500">ID:</span>
                        <span className="font-mono">{photo.id}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Row ID:</span>
                        <span className="font-mono">{photo.rowid}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Object ID:</span>
                        <span className="font-mono">{photo.objectId}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Type:</span>
                        <span>{photo.objectTypeName}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">File:</span>
                        <div className="text-xs break-all mt-1" title={photo.filename}>{photo.filename.split('\\').pop()}</div>
                      </div>
                      {photo.fileSize && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Size:</span>
                          <span>{(photo.fileSize / 1024).toFixed(1)} KB</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            </div>
          )}
        </div>
      </div>

      {/* Share Modal */}
      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        photoId={photo.id}
      />
    </div>
  );
};

export default PhotoModal;